<template>
    <div :class="toggleClasses" class="toggle" @click="toggleValue">
        <svg-icon name="check" class="toggle__check" />
    </div>
</template>

<script>
export default {
    props: {
        value: {
            type: [String, Boolean, Number],
            default: null,
        },
        onValue: {
            type: [String, Boolean, Number],
            default: true,
        },
        offValue: {
            type: [String, Boolean, Number],
            default: false,
        },
        size: {
            type: String,
            default: 'large',
            validator(value) {
                return ['medium', 'large'].includes(value);
            },
        },
        color: {
            type: String,
            default: 'black',
            validator(value) {
                return ['black', 'blue'].includes(value);
            },
        },
        showCheckmark: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        toggleClasses() {
            return {
                'is-on': this.value === this.onValue,
                [`toggle--${this.size}`]: this.size !== 'large',
                [`toggle--${this.color}`]: this.color !== 'black',
                'has-checkmark': this.showCheckmark,
                'is-disabled': this.disabled,
            };
        },
    },
    methods: {
        toggleValue() {
            if (this.disabled) {
                return;
            }

            const newValue = this.value === this.onValue ? this.offValue : this.onValue;

            this.$emit('input', newValue);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/_variables.scss';

$circle-size: 22px;
$edge-distance: 2px;
$toggle-width: 56px;
$toggle-height: 26px;
$toggle-on-distance: $toggle-width - $circle-size - $edge-distance;

.toggle {
    background-color: $gray;
    border-radius: 14px;
    cursor: pointer;
    display: inline-block;
    position: relative;
    height: $toggle-height;
    width: $toggle-width;
    vertical-align: top;
    margin-right: 10px;
    transition: all 250ms;

    &__check {
        $size: 18px;

        position: absolute;
        width: $size;
        height: $size;
        top: 50%;
        left: ($toggle-on-distance + $edge-distance + calc(($circle-size - $size) / 2)) - 1;
        transform: translateY(-50%);
        z-index: 1;
        color: $primary;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
    }

    &:before {
        content: '';
        display: block;
        position: absolute;
        transform: translateX($edge-distance);
        top: $edge-distance;
        height: $circle-size;
        width: $circle-size;
        border-radius: 50%;
        background-color: $white;
        box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.12);
        transition: all 250ms;
    }

    &.has-checkmark .toggle__check {
        visibility: visible;
    }

    &.is-on {
        background-color: $primary;

        &:before {
            box-shadow: -1px 0 1px 1px rgba(0, 0, 0, 0.2);
            transform: translateX($toggle-on-distance);
        }

        .toggle__check {
            opacity: 1;
            transition: opacity 100ms;
            transition-delay: 100ms;
        }
    }

    &.is-disabled {
        cursor: default;
        opacity: 0.6;
        pointer-events: none;

        &.is-on {
            background-color: $gray-dark;

            .toggle__check {
                color: $gray-dark;
            }
        }

        &:before {
            background-color: $gray-light;
        }
    }

    &--blue {
        &.is-on {
            background-color: $blue-dark;

            .toggle__check {
                color: $blue-dark;
            }
        }
    }

    &--medium {
        $circle-size: 20px;
        $toggle-width: 45px;
        $toggle-height: 24px;
        $toggle-on-distance: $toggle-width - $circle-size - $edge-distance;

        width: $toggle-width;
        min-width: $toggle-width;
        height: $toggle-height;
        min-width: $toggle-width;

        .toggle__check {
            left: ($toggle-on-distance + $edge-distance + calc(($circle-size - 18px) / 2)) - 1;
        }

        &:before {
            height: $circle-size;
            width: $circle-size;
        }

        &.is-on {
            &:before {
                transform: translateX($toggle-on-distance);
            }
        }
    }
}
</style>
